import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import {
  Observable,
  TimeoutError,
  catchError,
  finalize,
  from,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { IUser } from '../../models/user';
import { BootstrapService } from './bootstrap/bootstrap.service';
import { CleanupService } from './cleanup/cleanup.service';
import { TokenStorageService } from './tokens/token-storage.service';
import * as Sentry from "@sentry/angular";

interface LoginResponse {
  accessToken: string;
  user: LoginUser;
}

interface LoginUser {
  id: string;
  displayName: string;
  email: string;
  roles: unknown[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly bootstrapService = inject(BootstrapService);
  private readonly cleanupService = inject(CleanupService);

  private readonly authApiUrl = `${environment.apiBaseUrl}/auth`;

  // In-memory auth/session signals.
  private readonly accessToken = signal<string | null>(null);
  readonly isAuthenticated = signal<boolean | null>(null);
  private readonly loggedInUser = signal<IUser | null>(null);

  // Runtime guards for de-duped refresh + duplicate logout protection.
  private isLoggingOut = false;
  private inFlightRefresh$: Observable<string> | null = null;

  constructor() {
    this.initializeAuthStatus();
  }

  // ---------------------------
  // Public API (primary flow)
  // ---------------------------

  login(payload: {
    email: string;
    password: string;
    captchaToken: string;
    rememberMe: boolean;
    myhub: boolean;
  }): Observable<LoginResponse> {
    return this.postAuthRequest<LoginResponse>(`${this.authApiUrl}/signinV2`, payload).pipe(
      timeout(15000),
      switchMap((response) =>
        this.storeAccessToken(response.accessToken).pipe(
          tap(() => {
            this.loggedInUser.set(null);
            this.setAuthenticatedState();
          }),
          map(() => response),
        ),
      ),
      catchError((error: unknown) => {
        if (error instanceof TimeoutError) {
          return throwError(() => new Error('Login request timed out. Please try again.'));
        }
        return throwError(() => error);
      }),
    );
  }

  ensureAuthenticated(): Observable<boolean> {
    return this.getToken().pipe(
      switchMap((token) => {
        if (token) {
          this.setAuthenticatedState();
          return of(true);
        }

        return this.refreshToken().pipe(
          map(() => true),
          catchError(() => of(false)),
        );
      }),
      tap((isAuthenticated) => {
        if (!isAuthenticated) {
          this.isAuthenticated.set(false);
        }
      }),
    );
  }

  getToken(): Observable<string | null> {
    const token = this.accessToken();
    if (token && !this.tokenStorage.isTokenExpired()) {
      return of(token);
    }

    return this.tokenStorage.getToken().pipe(
      take(1),
      map((storedToken) => {
        if (!storedToken || this.tokenStorage.isTokenExpired()) {
          this.accessToken.set(null);
          return null;
        }

        this.accessToken.set(storedToken);
        return storedToken;
      }),
    );
  }

  refreshToken(): Observable<string> {
    // Single in-flight refresh for concurrent 401/missing-token requests.
    if (this.inFlightRefresh$) {
      return this.inFlightRefresh$;
    }

    this.inFlightRefresh$ = this.postAuthRequest<{ accessToken: string }>(`${this.authApiUrl}/refresh`, {}).pipe(
      switchMap((response) => this.storeAccessToken(response.accessToken).pipe(map(() => response.accessToken))),
      tap(() => this.setAuthenticatedState()),
      finalize(() => {
        this.inFlightRefresh$ = null;
      }),
      shareReplay(1),
    );

    return this.inFlightRefresh$;
  }

  logout(): void {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    this.clearSessionState();
    this.cleanupService.performLocalLogout(this.router.url);

    // Server logout is best-effort; local cleanup always runs.
    this.postAuthRequest<string>(`${this.authApiUrl}/user-logout`, {}, 'text')
      .pipe(
        timeout(5000),
        catchError(() => of('')),
        finalize(() => {
          this.isLoggingOut = false;
        }),
      )
      .subscribe();
  }

  // ---------------------------
  // Public API (user profile)
  // ---------------------------

  getCurrentUser(): Observable<IUser> {
    const existingUser = this.loggedInUser();
    if (existingUser) {
      return of(existingUser);
    }

    return this.http.get<IUser>(`${this.authApiUrl}/users/me`, { withCredentials: true }).pipe(
      tap((user) => this.loggedInUser.set(user)),
      catchError(() => throwError(() => new Error('Failed to fetch current user.'))),
    );
  }

  getLoggedInUsername(): string {
    return this.loggedInUser()?.username ?? '';
  }

  deactivateCurrentUser(): Observable<string> {
    return this.getCurrentUser().pipe(
      switchMap((user) =>
        this.http.post(`https://10.2.11.73/users/deactivateById/${user.userId}`, {}, { responseType: 'text' }),
      ),
      tap(() => this.logout()),
    );
  }

  /**
   * Invalidates the refresh token on the server (calls logout endpoint)
   * without clearing local session state. Debug/testing only.
   */
  invalidateRefreshToken(): Observable<string> {
    return this.postAuthRequest<string>(`${this.authApiUrl}/user-logout`, {}, 'text').pipe(timeout(5000));
  }

  // ---------------------------
  // Private helpers
  // ---------------------------

  private initializeAuthStatus(): void {
    // Unknown state until first token/refresh resolution completes.
    this.isAuthenticated.set(null);

    this.ensureAuthenticated()
      .pipe(take(1))
      .subscribe({
        next: (isAuthenticated) => {
          if (!isAuthenticated) {
            this.isAuthenticated.set(false);
          }
        },
        error: () => {
          this.isAuthenticated.set(false);
        },
      });
  }

  private setAuthenticatedState(): void {
    const wasAuthenticated = this.isAuthenticated() === true;
    this.isAuthenticated.set(true);

    // Fire post-login bootstrap only once per auth session transition.
    if (!wasAuthenticated) {
      this.bootstrapService.dispatchPostLoginActions();
      this.trackUserSessions(); // should trigger on login only to track successfull logins
    }
  }

  private clearSessionState(): void {
    this.tokenStorage.removeToken();
    this.accessToken.set(null);
    this.loggedInUser.set(null);
    this.isAuthenticated.set(false);
  }

  private storeAccessToken(token: string): Observable<void> {
    return this.tokenStorage.saveToken(token).pipe(tap(() => this.accessToken.set(token)));
  }

  private postAuthRequest<T>(url: string, body: unknown, responseType: 'json' | 'text' = 'json'): Observable<T> {
    // Native path via CapacitorHttp for reliable cookie transport (iOS + Android).
    if (this.isNativePlatform()) {
      return from(
        CapacitorHttp.post({
          url,
          data: body ?? {},
          headers: {
            'Content-Type': 'application/json',
          },
          responseType: responseType === 'text' ? 'text' : 'json',
          webFetchExtra: { credentials: 'include' },
        }),
      ).pipe(
        switchMap((response) => {
          // CapacitorHttp doesn't throw on non-2xx — translate to HttpErrorResponse
          // so interceptor error handlers (e.g. isRefreshTokenInvalidError) work.
          if (response.status < 200 || response.status >= 300) {
            return throwError(
              () =>
                new HttpErrorResponse({
                  status: response.status,
                  statusText: response.url,
                  url,
                  error: response.data,
                }),
            );
          }
          return of(response.data as T);
        }),
      );
    }

    if (responseType === 'text') {
      return this.http.post(url, body, {
        withCredentials: true,
        responseType: 'text',
      }) as Observable<T>;
    }

    return this.http.post<T>(url, body, { withCredentials: true });
  }

  private isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  trackUserSessions() {
    this.getCurrentUser().subscribe(user => {
      Sentry.setUser({ id: user?.userId ?? 'unknown' });
      Sentry.startSession();
      Sentry.captureSession(true);
    })
  }
}
